import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Brain, Loader2, Play, CheckCircle2, XCircle, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});
const SEV = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-gray-100 text-gray-600' };

const AIOpsPage = () => {
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [recs, setRecs] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [ctx, setCtx] = useState('{\n  "sla_breaches": 2,\n  "pending_docs": 8,\n  "unassigned_queue": 0\n}');
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ai-ops/recommendations`, { headers: authHeaders() });
      if (res.status === 403) { setAvailable(false); return; }
      if (!res.ok) throw new Error('load failed');
      setRecs((await res.json()).recommendations || []);
      setAvailable(true);
    } catch { /* keep prior */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const analyze = async () => {
    setMsg(''); setAnalysis(null);
    let parsed;
    try { parsed = JSON.parse(ctx); } catch { setMsg('Context must be valid JSON.'); return; }
    const res = await fetch(`${API_URL}/api/ai-ops/analyze`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ context: parsed }),
    });
    if (!res.ok) { setMsg('Analyze failed'); return; }
    setAnalysis(await res.json());
  };

  const decide = async (rec, action) => {
    const res = await fetch(`${API_URL}/api/ai-ops/recommendations/${rec.id}/${action}`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({ note: '' }),
    });
    if (res.status === 403) { setMsg(`${action} requires Super Admin.`); return; }
    load();
  };

  const persist = async (r) => {
    const res = await fetch(`${API_URL}/api/ai-ops/recommendations`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ title: r.title, category: r.category, severity: r.severity, reasoning: r.reasoning, suggested_action: r.suggested_action }),
    });
    if (res.status === 403) { setMsg('Saving a recommendation requires Super Admin.'); return; }
    setMsg('Recommendation saved for review.'); load();
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="ai-ops-page">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Brain className="w-7 h-7 text-fuchsia-600" /> AI-Assisted Operations
          </h1>
          <p className="text-gray-600 mt-1">
            Explainable, advisory recommendations. No external AI is used and nothing runs automatically — a human approves every recommendation.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12" data-testid="ai-ops-loading"><Loader2 className="w-8 h-8 animate-spin text-fuchsia-600" /></div>
        ) : !available ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-testid="ai-ops-unavailable">
            <p className="text-gray-700">AI-Assisted Operations is not currently available.</p>
          </div>
        ) : (
          <>
            {msg && <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800" data-testid="ai-ops-msg">{msg}</div>}

            <div className="flex items-center gap-2 bg-fuchsia-50 border border-fuchsia-200 rounded-lg p-3 text-fuchsia-800 text-sm" data-testid="ai-ops-advisory-banner">
              <Info className="w-5 h-5" /> Advisory only — recommendations require explicit human approval and execute nothing.
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3" data-testid="ai-ops-analyze-panel">
              <h2 className="font-semibold text-gray-900">Analyze Operational Context</h2>
              <textarea value={ctx} onChange={(e) => setCtx(e.target.value)} rows={4} data-testid="ai-ops-context-input"
                className="border rounded-lg px-3 py-2 text-sm w-full font-mono" />
              <button onClick={analyze} data-testid="ai-ops-analyze-btn"
                className="inline-flex items-center gap-2 bg-fuchsia-600 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-fuchsia-700 transition-colors">
                <Play className="w-4 h-4" /> Analyze
              </button>
              {analysis && (
                <div className="space-y-2" data-testid="ai-ops-analysis-result">
                  <p className="text-xs text-gray-500">{analysis.recommendations.length} recommendation(s) · automation rules considered: {String(analysis.automation_rules_considered)}</p>
                  {analysis.recommendations.map((r, i) => (
                    <div key={i} data-testid={`ai-ops-analysis-rec-${i}`} className="rounded-lg border border-gray-200 p-3">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">{r.title}</span>
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${SEV[r.severity]}`}>{r.severity}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">source: {r.source} · action: {r.suggested_action?.type}</p>
                      <details className="mt-1"><summary className="text-xs text-fuchsia-600 cursor-pointer">Why?</summary>
                        <pre className="text-[11px] text-gray-600 whitespace-pre-wrap mt-1">{JSON.stringify(r.reasoning, null, 2)}</pre>
                      </details>
                      <button onClick={() => persist(r)} data-testid={`ai-ops-save-rec-${i}`} className="mt-2 text-xs text-fuchsia-600 hover:underline">Save for review</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2" data-testid="ai-ops-recs-list">
              <h2 className="font-semibold text-gray-900">Saved Recommendations ({recs.length})</h2>
              {recs.length === 0 ? (
                <div className="text-center py-8 text-gray-400" data-testid="ai-ops-empty">No saved recommendations.</div>
              ) : recs.map((r) => (
                <div key={r.id} data-testid={`ai-ops-rec-${r.id}`} className="rounded-lg border border-gray-200 bg-white p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{r.title}</span>
                        <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${SEV[r.severity]}`}>{r.severity}</span>
                        <span data-testid={`ai-ops-rec-status-${r.id}`} className="text-[10px] uppercase text-gray-400">{r.status}</span>
                      </div>
                      <details className="mt-1"><summary className="text-xs text-fuchsia-600 cursor-pointer">Reasoning</summary>
                        <pre className="text-[11px] text-gray-600 whitespace-pre-wrap mt-1">{JSON.stringify(r.reasoning, null, 2)}</pre>
                      </details>
                    </div>
                    {r.status === 'pending' && (
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        <button onClick={() => decide(r, 'approve')} data-testid={`ai-ops-approve-${r.id}`} className="inline-flex items-center gap-1 text-xs text-green-600 hover:underline"><CheckCircle2 className="w-4 h-4" /> Approve</button>
                        <button onClick={() => decide(r, 'dismiss')} data-testid={`ai-ops-dismiss-${r.id}`} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"><XCircle className="w-4 h-4" /> Dismiss</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AIOpsPage;
